import { type FormEvent, useEffect, useRef, useState } from "react";
import config from "../contact-config.json";
import { type Locale, messages } from "./i18n";

// Only a same-origin API path is public. Sender/recipient stay server-side.
if (config.endpoint !== "/api/contact") {
  throw new Error("Contact configuration requires the same-origin contact API");
}

type Transport = "unknown" | "preview" | "brevo";
type Status = "pending" | "saved" | "accepted" | "unknown" | "failed" | null;

export default function ContactForm({ locale }: { locale: Locale }) {
  const t = messages[locale];
  const [status, setStatus] = useState<Status>(null);
  const [transport, setTransport] = useState<Transport>("unknown");
  const [checking, setChecking] = useState(true);
  const request = useRef<{ payload: string; key: string } | null>(null);
  const inFlight = useRef(false);
  const confirmation = useRef<HTMLOutputElement>(null);
  const busy = status === "pending";
  const deliveryReady = transport === "brevo";

  useEffect(() => {
    const controller = new AbortController();
    void fetch(`${config.endpoint}/health`, {
      signal: AbortSignal.any([controller.signal, AbortSignal.timeout(5000)]),
      cache: "no-store",
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((health) => {
        const durableStatusAvailable =
          health?.storesSubmissions === true ||
          (health?.storesSubmissions === false && health?.idempotency === "d1");
        setTransport(
          health?.status === "ok" && durableStatusAvailable
            ? health.transport === "brevo" && health.sendsEmail === true
              ? "brevo"
              : health.transport === "preview" && health.sendsEmail === false
                ? "preview"
                : "unknown"
            : "unknown",
        );
      })
      .catch(() => setTransport("unknown"))
      .finally(() => setChecking(false));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    // Focus after React commits the confirmation, bringing it into view on mobile.
    if (status === "accepted") confirmation.current?.focus();
  }, [status]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      inFlight.current ||
      busy ||
      transport === "unknown" ||
      status === "accepted" ||
      status === "saved"
    )
      return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const contact = Object.fromEntries(
      ["name", "email", "subject", "message"].map((key) => [
        key,
        String(data.get(key) || "").trim(),
      ]),
    );
    for (const key of ["name", "email", "subject", "message"]) {
      const input = form.elements.namedItem(key) as
        | HTMLInputElement
        | HTMLTextAreaElement;
      input.setCustomValidity(contact[key] ? "" : t.contactWhitespace);
    }
    if (!form.reportValidity()) return;
    const payload = JSON.stringify({
      ...contact,
      locale,
      website: String(data.get("website") || ""),
    });
    // Rechecking an uncertain response reuses the key, never sends a new message.
    if (request.current?.payload !== payload)
      request.current = { payload, key: crypto.randomUUID() };
    const submission = request.current;
    // A ref closes the gap before React renders disabled controls.
    inFlight.current = true;
    setStatus("pending");
    try {
      const response = await fetch(config.endpoint, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": submission.key,
          "x-contact-transport": transport,
        },
        body: payload,
        signal: AbortSignal.timeout(12000),
      });
      const result = await response.json();
      const trackedStatus =
        result?.stored === true ||
        (result?.stored === false && result?.idempotency === "d1");
      if (
        response.ok &&
        result?.requestId === submission.key &&
        trackedStatus &&
        result.delivered === false &&
        result.transport === transport
      ) {
        if (
          deliveryReady &&
          result.status === "provider_accepted" &&
          result.accepted === true
        ) {
          // Only the matching, idempotently tracked provider acceptance clears the draft.
          form.reset();
          request.current = null;
          setStatus("accepted");
          return;
        }
        if (
          !deliveryReady &&
          result.status === "stored_not_sent" &&
          result.sent === false
        ) {
          setStatus("saved");
          return;
        }
      }
      if (!response.ok && result.status === "error" && result.sent === false) {
        if (
          result.code === "provider_outcome_unknown" ||
          result.code === "provider_redirected"
        ) {
          setStatus("unknown");
        } else {
          // A definitive rejection did not send; a later user retry needs a new key.
          request.current = null;
          setStatus("failed");
        }
        if (result.code === "transport_changed") setTransport("unknown");
      } else setStatus("unknown");
    } catch {
      // A lost response is not proof that a stored message was not sent.
      setStatus("unknown");
    } finally {
      inFlight.current = false;
    }
  }

  return (
    <div className="contact-panel" data-contact-transport={transport}>
      <p id="contact-required" className="contact-help">
        {t.contactRequired}
      </p>
      <p id="contact-service-note" className="contact-help" aria-live="polite">
        {checking
          ? t.contactChecking
          : deliveryReady
            ? t.contactDeliveryNote
            : transport === "preview"
              ? t.contactPreviewNote
              : t.contactTransportUnknown}
      </p>
      <form
        className="contact-form"
        aria-describedby="contact-note contact-required contact-service-note"
        aria-busy={busy}
        onSubmit={submit}
        onInput={(event) => {
          const input = event.target;
          if (
            input instanceof HTMLInputElement ||
            input instanceof HTMLTextAreaElement
          )
            input.setCustomValidity("");
          if (!busy) setStatus(null);
        }}
      >
        <label className="visually-hidden" aria-hidden="true">
          Website
          <input
            name="website"
            autoComplete="off"
            tabIndex={-1}
            disabled={busy}
          />
        </label>
        <div className="contact-fields">
          <label htmlFor="contact-name">
            {t.contactName}
            <input
              id="contact-name"
              name="name"
              autoComplete="name"
              required
              maxLength={120}
              disabled={busy}
            />
          </label>
          <label htmlFor="contact-email">
            {t.contactEmail}
            <input
              id="contact-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              maxLength={254}
              disabled={busy}
            />
          </label>
        </div>
        <label htmlFor="contact-subject">
          {t.contactSubject}
          <input
            id="contact-subject"
            name="subject"
            required
            maxLength={160}
            disabled={busy}
          />
        </label>
        <label htmlFor="contact-message">
          {t.contactMessage}
          <textarea
            id="contact-message"
            name="message"
            rows={7}
            required
            maxLength={2000}
            disabled={busy}
          />
        </label>
        <button
          className="contact-button"
          type="submit"
          disabled={
            busy ||
            transport === "unknown" ||
            status === "accepted" ||
            status === "saved" ||
            status === "unknown"
          }
        >
          {busy
            ? deliveryReady
              ? t.contactSending
              : t.contactSaving
            : status === "unknown"
              ? t.contactUnknownButton
              : deliveryReady
                ? t.contactSend
                : t.contactSavePreview}{" "}
          ↗
        </button>
      </form>
      <output
        ref={confirmation}
        className="contact-status"
        tabIndex={-1}
        aria-live="polite"
        aria-atomic="true"
        data-contact-status={status ?? "idle"}
      >
        {status === "saved" ? (
          t.contactSavedPreview
        ) : status === "accepted" ? (
          <>
            <strong className="contact-success-title">
              <span aria-hidden="true">✓ </span>
              {t.contactSent}
            </strong>
            <span>{t.contactAccepted}</span>
          </>
        ) : status === "unknown" ? (
          t.contactUnknown
        ) : status === "failed" ? (
          t.contactDeliveryFailed
        ) : (
          ""
        )}
      </output>
    </div>
  );
}
